import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthSession } from '../../contexts/AuthSessionContext';
import { ensureActiveSession } from '../../services/session';

const SESSION_POLL_INTERVAL_MS = 120000;

const SessionGuard = ({ enabled = true }) => {
  const location = useLocation();
  const { isAuthenticated } = useAuthSession();

  useEffect(() => {
    if (!enabled || !isAuthenticated || typeof window === 'undefined') {
      return undefined;
    }

    let disposed = false;

    const validateSession = ({ force = false } = {}) => {
      if (disposed || !isAuthenticated) return Promise.resolve(false);
      return ensureActiveSession({ force }).catch(() => false);
    };

    void validateSession({ force: true });

    const handleFocus = () => {
      void validateSession({ force: true });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void validateSession({ force: true });
      }
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') {
        void validateSession();
      }
    }, SESSION_POLL_INTERVAL_MS);

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, isAuthenticated, location.pathname]);

  return null;
};

export default SessionGuard;
